
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { MedicationFormFields } from "./form/MedicationFormFields";
import { MedicationFormActions } from "./form/MedicationFormActions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormValues } from "./MedicationForm"; // Import the FormValues interface from MedicationForm

interface MedicationEditFormProps {
  medication: {
    id: string;
    name: string;
    dosage: string;
    frequency?: string;
    start_date?: string;
    end_date?: string | null;
    expiry_date?: string | null;
    notes?: string | null;
    reminder_time?: string[];
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MedicationEditForm = ({ medication, open, onOpenChange }: MedicationEditFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<FormValues>({
    defaultValues: {
      name: medication?.name || "",
      dosage: medication?.dosage || "",
      frequency: medication?.frequency || "",
      start_date: medication?.start_date || new Date().toISOString().split('T')[0],
      end_date: medication?.end_date || "",
      expiry_date: medication?.expiry_date || "",
      notes: medication?.notes || "",
      reminder_time: medication?.reminder_time || [],
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!medication) return;
    
    setIsLoading(true);
    try {
      // Ensure frequency is always a string, even if it's optional in the type
      const medicationData = {
        ...values,
        frequency: values.frequency || "", // Ensure it's not undefined
        expiry_date: values.expiry_date || null,
        notes: values.notes || null,
        reminder_time: values.reminder_time || [],
        start_date: values.start_date || new Date().toISOString().split('T')[0],
      };
      
      const { error } = await supabase
        .from("medications")
        .update(medicationData)
        .eq("id", medication.id);

      if (error) throw error;

      toast({
        title: "Médicament modifié",
        description: "Le médicament a été modifié avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      onOpenChange(false);
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier le médicament",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] h-[90vh]">
        <DialogHeader>
          <DialogTitle>Modifier le médicament</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <MedicationFormFields form={form} />
              <MedicationFormActions 
                onCancel={() => onOpenChange(false)}
                isLoading={isLoading}
                submitLabel="Modifier"
              />
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
